import { useEffect, useRef, useState, type RefObject } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  tokens,
  useMergedRefs,
  useRestoreFocusTarget,
} from '@coreai/fluentui-react';
import { AddRegular, DismissRegular, DocumentBulletListRegular, SparkleRegular } from '@fluentui/react-icons';
import { WorkflowBuilderHero } from '@/components/WorkflowBuilderHero';
import { FeatureErrorBoundary } from '@/components/ErrorBoundary';
import { useTelemetry } from '@/hooks/useTelemetry';
import { openCreateWorkflowDialog } from '@/stores/createWorkflowDialogStore';

const useStyles = makeStyles({
  surface: {
    width: '640px',
    maxWidth: `calc(100vw - ${tokens.spacingHorizontalXXL})`,
  },
});

interface WorkflowCreateMenuProps {
  canCreate: boolean;
  isAssistantEnabled: boolean;
  isTemplatesEnabled: boolean;
  onBuildFromTemplate: () => void;
  fallbackFocusRef: RefObject<HTMLButtonElement>;
}

export const WorkflowCreateMenu = ({
  canCreate,
  isAssistantEnabled,
  isTemplatesEnabled,
  onBuildFromTemplate,
  fallbackFocusRef,
}: WorkflowCreateMenuProps) => {
  const styles = useStyles();
  const { trackEvent } = useTelemetry();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const setTriggerRef = useMergedRefs<HTMLButtonElement>(triggerRef);
  const restoreFocusTarget = useRestoreFocusTarget();
  const promptRef = useRef<HTMLTextAreaElement>(null);
  const [isAIOpen, setIsAIOpen] = useState(false);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  const canBuildWithAI = canCreate && isAssistantEnabled;

  useEffect(() => {
    if (!canBuildWithAI) {
      if (isAIOpen) (triggerRef.current ?? fallbackFocusRef.current)?.focus();
      setIsAIOpen(false);
      setPendingPrompt(null);
    }
  }, [canBuildWithAI, isAIOpen, fallbackFocusRef]);

  useEffect(() => {
    if (pendingPrompt === null) return;
    setPendingPrompt(null);
    if (!canBuildWithAI) return;
    // The prompt modal is unmounted before opening the global naming modal.
    // Focus the persistent trigger so the naming modal can restore focus to it.
    triggerRef.current?.focus();
    openCreateWorkflowDialog({ prompt: pendingPrompt, source: 'workflow-list-toolbar' });
  }, [pendingPrompt, canBuildWithAI]);

  const dismissAI = () => {
    setIsAIOpen(false);
    triggerRef.current?.focus();
  };

  const trackEntry = (method: 'templates' | 'ai' | 'scratch') => {
    trackEvent('workflow.create.entry_selected', { source: 'workflow-list-toolbar', method });
  };

  return (
    <>
      {canCreate && (
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <MenuButton {...restoreFocusTarget} ref={setTriggerRef} appearance="primary" size="small" icon={<AddRegular />}>
              Create workflow
            </MenuButton>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {isTemplatesEnabled && (
                <MenuItem icon={<DocumentBulletListRegular />} onClick={() => {
                  trackEntry('templates');
                  triggerRef.current?.focus();
                  onBuildFromTemplate();
                }}>
                  Build from templates
                </MenuItem>
              )}
              {isAssistantEnabled && (
                <MenuItem icon={<SparkleRegular />} onClick={() => {
                  trackEntry('ai');
                  triggerRef.current?.focus();
                  setIsAIOpen(true);
                }}>
                  Build with AI
                </MenuItem>
              )}
              <MenuItem icon={<AddRegular />} onClick={() => {
                trackEntry('scratch');
                triggerRef.current?.focus();
                openCreateWorkflowDialog({ source: 'workflow-list-toolbar' });
              }}>
                Start from scratch
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      )}
      {isAIOpen && canBuildWithAI && (
        <Dialog
          open
          onOpenChange={(_, data) => { if (!data.open) dismissAI(); }}
          surfaceMotion={{
            onMotionFinish: (_, { direction }) => {
              // The animated surface mounts after the menu owner effect.
              if (direction === 'enter') promptRef.current?.focus();
            },
          }}
        >
          <DialogSurface className={styles.surface}>
            <DialogBody>
              <DialogTitle action={
                <Button appearance="subtle" icon={<DismissRegular />} aria-label="Close" onClick={dismissAI} />
              }>
                Build with AI
              </DialogTitle>
              <DialogContent>
                <FeatureErrorBoundary componentName="WorkflowAICreate">
                  <WorkflowBuilderHero
                    hasWorkflows
                    presentation="dialog"
                    promptRef={promptRef}
                    isCreating={false}
                    error={null}
                    onBuild={(prompt) => {
                      setIsAIOpen(false);
                      setPendingPrompt(prompt);
                    }}
                  />
                </FeatureErrorBoundary>
              </DialogContent>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </>
  );
};
